import i18n from "i18next";
import { initReactI18next } from "react-i18next";

export const supportedLanguages = [
  { code: "pt-BR", googleCode: "pt", label: "Português", shortLabel: "PT", dir: "ltr" },
  { code: "en", googleCode: "en", label: "English", shortLabel: "EN", dir: "ltr" },
  { code: "es", googleCode: "es", label: "Español", shortLabel: "ES", dir: "ltr" },
  { code: "fr", googleCode: "fr", label: "Français", shortLabel: "FR", dir: "ltr" },
  { code: "ar", googleCode: "ar", label: "العربية", shortLabel: "AR", dir: "rtl" },
  { code: "de", googleCode: "de", label: "Deutsch", shortLabel: "DE", dir: "ltr" },
  { code: "zh-CN", googleCode: "zh-CN", label: "简体中文", shortLabel: "ZH", dir: "ltr" }
] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number]["code"];

const languageStorageKey = "geoanalytics.language";
const defaultLanguage: SupportedLanguage = "pt-BR";

const languageCodes = supportedLanguages.map((language) => language.code);

export function getSupportedLanguage(languageCode?: string | null): SupportedLanguage {
  if (!languageCode) {
    return defaultLanguage;
  }

  if (languageCodes.includes(languageCode as SupportedLanguage)) {
    return languageCode as SupportedLanguage;
  }

  const baseLanguage = languageCode.split("-")[0];
  const match = supportedLanguages.find((language) => language.code.split("-")[0] === baseLanguage);

  return match?.code ?? defaultLanguage;
}

export function getLanguageMeta(languageCode: string) {
  return (
    supportedLanguages.find((language) => language.code === getSupportedLanguage(languageCode)) ??
    supportedLanguages[0]
  );
}

function getInitialLanguage() {
  try {
    const storedLanguage = window.localStorage.getItem(languageStorageKey);

    if (storedLanguage) {
      return getSupportedLanguage(storedLanguage);
    }
  } catch {
    // Local storage can be unavailable in restricted browser modes.
  }

  return getSupportedLanguage(window.navigator.language);
}

const resources = {
  "pt-BR": {
    translation: {
      common: {
        appName: "GeoAnalytics",
        loadingGeoAnalytics: "Carregando GeoAnalytics",
        loadingPage: "Carregando página",
        verifyAccess: "Verificando acesso"
      },
      language: {
        change: "Alterar idioma",
        current: "Idioma atual: {{language}}",
        menuTitle: "Idioma"
      },
      publicNav: {
        about: "Sobre nós",
        login: "Entrar",
        start: "Começar"
      },
      login: {
        title: "Entrar",
        eyebrow: "Sua parceira para decisões estratégicas",
        headline: "GeoAnalytics",
        subtitle:
          "Cruzamento inteligente de dados territoriais para decisões mais rápidas, precisas e transparentes.",
        email: "E-mail",
        password: "Senha",
        submit: "Entrar",
        google: "Entrar com Google",
        createAccount: "Criar conta",
        recoverPassword: "Recuperar senha",
        divider: "ou",
        errorEmail: "Não foi possível entrar com os dados informados.",
        errorTemporary: "Erro temporário ao entrar.",
        verifying: "Verificando sua conta",
        secureAccess: "Acesso seguro",
        secureAccessText: "Login com Firebase, Google e e-mail/senha.",
        webVersion: "Versão web",
        mobileVersion: "Versão smartphone"
      },
      shell: {
        profileTooltip: "Perfil do usuário",
        profileAria: "Abrir perfil do usuário",
        signOut: "Sair",
        notifications: "Notificações",
        openNotifications: "Abrir notificações"
      },
      nav: {
        dashboard: "Painel",
        projects: "Processos",
        newProcess: "Novo processo",
        studio: "Studio REURB",
        teams: "Equipes",
        map: "Mapa",
        clients: "Clientes",
        registries: "Cadastros",
        documents: "Documentos",
        tasks: "Tarefas",
        reports: "Relatórios",
        notifications: "Notificações",
        support: "Suporte",
        user: "Usuário",
        administration: "Administração",
        adminArea: "Área administrativa",
        adminOverview: "Visão geral",
        adminUsers: "Usuários",
        adminTeams: "Equipes",
        adminProjects: "Processos",
        adminTickets: "Tickets",
        adminPlans: "Planos",
        adminAudit: "Auditoria",
        adminSettings: "Configurações"
      }
    }
  },
  en: {
    translation: {
      common: {
        loadingGeoAnalytics: "Loading GeoAnalytics",
        loadingPage: "Loading page",
        verifyAccess: "Checking access"
      },
      language: {
        change: "Change language",
        current: "Current language: {{language}}",
        menuTitle: "Language"
      },
      publicNav: {
        about: "About the company",
        login: "Sign in",
        start: "Start"
      },
      login: {
        title: "Sign in",
        eyebrow: "Territorial intelligence for real operations",
        headline: "GeoAnalytics",
        subtitle: "Access maps, projects, documents, and teams in a smoother visual experience.",
        email: "Email",
        password: "Password",
        submit: "Sign in",
        google: "Sign in with Google",
        createAccount: "Create account",
        recoverPassword: "Recover password",
        divider: "or",
        errorEmail: "We could not sign you in with these credentials.",
        errorTemporary: "Temporary sign-in error.",
        verifying: "Checking your account",
        secureAccess: "Secure access",
        secureAccessText: "Firebase, Google, and email/password login.",
        webVersion: "Web version",
        mobileVersion: "Smartphone version"
      },
      shell: {
        profileTooltip: "User profile",
        profileAria: "Open user profile",
        signOut: "Sign out",
        notifications: "Notifications",
        openNotifications: "Open notifications"
      },
      nav: {
        dashboard: "Dashboard",
        projects: "Projects",
        newProcess: "New process",
        studio: "REURB Studio",
        teams: "Teams",
        map: "Map",
        clients: "Clients",
        registries: "Registries",
        documents: "Documents",
        tasks: "Tasks",
        reports: "Reports",
        notifications: "Notifications",
        support: "Support",
        user: "User",
        administration: "Administration",
        adminArea: "Administration",
        adminOverview: "Overview",
        adminUsers: "Users",
        adminTeams: "Teams",
        adminProjects: "Projects",
        adminTickets: "Tickets",
        adminPlans: "Plans",
        adminAudit: "Audit",
        adminSettings: "Settings"
      }
    }
  },
  es: {
    translation: {
      common: { loadingGeoAnalytics: "Cargando GeoAnalytics", loadingPage: "Cargando página" },
      language: {
        change: "Cambiar idioma",
        current: "Idioma actual: {{language}}",
        menuTitle: "Idioma"
      },
      publicNav: { about: "Conoce la empresa", login: "Entrar", start: "Comenzar" },
      login: {
        title: "Entrar",
        eyebrow: "Inteligencia territorial para operaciones reales",
        headline: "GeoAnalytics",
        subtitle: "Accede a mapas, proyectos, documentos y equipos con una experiencia más fluida.",
        email: "Correo",
        password: "Contraseña",
        submit: "Entrar",
        google: "Entrar con Google",
        createAccount: "Crear cuenta",
        recoverPassword: "Recuperar contraseña",
        divider: "o",
        verifying: "Verificando tu cuenta",
        secureAccess: "Acceso seguro",
        secureAccessText: "Login con Firebase, Google y correo/contraseña.",
        webVersion: "Versión web",
        mobileVersion: "Versión smartphone"
      },
      shell: {
        profileTooltip: "Perfil de usuario",
        profileAria: "Abrir perfil",
        signOut: "Salir",
        notifications: "Notificaciones",
        openNotifications: "Abrir notificaciones"
      },
      nav: {
        dashboard: "Panel",
        projects: "Proyectos",
        newProcess: "Nuevo proceso",
        studio: "Studio REURB",
        teams: "Equipos",
        map: "Mapa",
        clients: "Clientes",
        registries: "Registros",
        documents: "Documentos",
        tasks: "Tareas",
        reports: "Informes",
        notifications: "Notificaciones",
        support: "Soporte",
        user: "Usuario",
        administration: "Administración",
        adminArea: "Administración",
        adminOverview: "Vista general",
        adminUsers: "Usuarios",
        adminTeams: "Equipos",
        adminProjects: "Proyectos",
        adminTickets: "Tickets",
        adminPlans: "Planes",
        adminAudit: "Auditoría",
        adminSettings: "Configuración"
      }
    }
  },
  fr: {
    translation: {
      common: {
        loadingGeoAnalytics: "Chargement de GeoAnalytics",
        loadingPage: "Chargement de la page"
      },
      language: {
        change: "Changer de langue",
        current: "Langue actuelle : {{language}}",
        menuTitle: "Langue"
      },
      publicNav: { about: "Découvrir l'entreprise", login: "Connexion", start: "Commencer" },
      login: {
        title: "Connexion",
        eyebrow: "Intelligence territoriale pour les opérations réelles",
        headline: "GeoAnalytics",
        subtitle:
          "Accédez aux cartes, projets, documents et équipes dans une expérience plus fluide.",
        email: "E-mail",
        password: "Mot de passe",
        submit: "Connexion",
        google: "Connexion avec Google",
        createAccount: "Créer un compte",
        recoverPassword: "Récupérer le mot de passe",
        divider: "ou",
        verifying: "Vérification de votre compte",
        secureAccess: "Accès sécurisé",
        secureAccessText: "Connexion Firebase, Google et e-mail/mot de passe.",
        webVersion: "Version web",
        mobileVersion: "Version smartphone"
      },
      shell: {
        profileTooltip: "Profil utilisateur",
        profileAria: "Ouvrir le profil",
        signOut: "Se déconnecter",
        notifications: "Notifications",
        openNotifications: "Ouvrir les notifications"
      },
      nav: {
        dashboard: "Tableau",
        projects: "Projets",
        newProcess: "Nouveau processus",
        studio: "Studio REURB",
        teams: "Équipes",
        map: "Carte",
        clients: "Clients",
        registries: "Registres",
        documents: "Documents",
        tasks: "Tâches",
        reports: "Rapports",
        notifications: "Notifications",
        support: "Support",
        user: "Utilisateur",
        administration: "Administration",
        adminArea: "Administration",
        adminOverview: "Vue générale",
        adminUsers: "Utilisateurs",
        adminTeams: "Équipes",
        adminProjects: "Projets",
        adminTickets: "Tickets",
        adminPlans: "Plans",
        adminAudit: "Audit",
        adminSettings: "Paramètres"
      }
    }
  },
  ar: {
    translation: {
      common: { loadingGeoAnalytics: "جاري تحميل GeoAnalytics", loadingPage: "جاري تحميل الصفحة" },
      language: {
        change: "تغيير اللغة",
        current: "اللغة الحالية: {{language}}",
        menuTitle: "اللغة"
      },
      publicNav: { about: "عن الشركة", login: "تسجيل الدخول", start: "ابدأ" },
      login: {
        title: "تسجيل الدخول",
        eyebrow: "ذكاء مكاني للعمليات الفعلية",
        headline: "GeoAnalytics",
        subtitle: "ادخل إلى الخرائط والمشاريع والمستندات والفرق بتجربة أكثر سلاسة.",
        email: "البريد الإلكتروني",
        password: "كلمة المرور",
        submit: "دخول",
        google: "الدخول باستخدام Google",
        createAccount: "إنشاء حساب",
        recoverPassword: "استعادة كلمة المرور",
        divider: "أو",
        verifying: "جاري التحقق من حسابك",
        secureAccess: "وصول آمن",
        secureAccessText: "تسجيل دخول عبر Firebase وGoogle والبريد/كلمة المرور.",
        webVersion: "نسخة الويب",
        mobileVersion: "نسخة الهاتف"
      },
      shell: {
        profileTooltip: "ملف المستخدم",
        profileAria: "فتح ملف المستخدم",
        signOut: "تسجيل الخروج",
        notifications: "الإشعارات",
        openNotifications: "فتح الإشعارات"
      },
      nav: {
        dashboard: "لوحة التحكم",
        projects: "المشاريع",
        newProcess: "عملية جديدة",
        studio: "استوديو REURB",
        teams: "الفرق",
        map: "الخريطة",
        clients: "العملاء",
        registries: "السجلات",
        documents: "المستندات",
        tasks: "المهام",
        reports: "التقارير",
        notifications: "الإشعارات",
        support: "الدعم",
        user: "المستخدم",
        administration: "الإدارة",
        adminArea: "الإدارة",
        adminOverview: "نظرة عامة",
        adminUsers: "المستخدمون",
        adminTeams: "الفرق",
        adminProjects: "المشاريع",
        adminTickets: "التذاكر",
        adminPlans: "الخطط",
        adminAudit: "التدقيق",
        adminSettings: "الإعدادات"
      }
    }
  },
  de: {
    translation: {
      common: {
        loadingGeoAnalytics: "GeoAnalytics wird geladen",
        loadingPage: "Seite wird geladen"
      },
      language: {
        change: "Sprache ändern",
        current: "Aktuelle Sprache: {{language}}",
        menuTitle: "Sprache"
      },
      publicNav: { about: "Unternehmen", login: "Anmelden", start: "Starten" },
      login: {
        title: "Anmelden",
        eyebrow: "Territoriale Intelligenz für reale Abläufe",
        headline: "GeoAnalytics",
        subtitle: "Karten, Projekte, Dokumente und Teams in einer flüssigeren Oberfläche nutzen.",
        email: "E-Mail",
        password: "Passwort",
        submit: "Anmelden",
        google: "Mit Google anmelden",
        createAccount: "Konto erstellen",
        recoverPassword: "Passwort zurücksetzen",
        divider: "oder",
        verifying: "Konto wird geprüft",
        secureAccess: "Sicherer Zugriff",
        secureAccessText: "Login mit Firebase, Google und E-Mail/Passwort.",
        webVersion: "Webversion",
        mobileVersion: "Smartphone-Version"
      },
      shell: {
        profileTooltip: "Benutzerprofil",
        profileAria: "Benutzerprofil öffnen",
        signOut: "Abmelden",
        notifications: "Benachrichtigungen",
        openNotifications: "Benachrichtigungen öffnen"
      },
      nav: {
        dashboard: "Dashboard",
        projects: "Projekte",
        newProcess: "Neuer Prozess",
        studio: "REURB-Studio",
        teams: "Teams",
        map: "Karte",
        clients: "Kunden",
        registries: "Register",
        documents: "Dokumente",
        tasks: "Aufgaben",
        reports: "Berichte",
        notifications: "Benachrichtigungen",
        support: "Support",
        user: "Benutzer",
        administration: "Administration",
        adminArea: "Administration",
        adminOverview: "Übersicht",
        adminUsers: "Benutzer",
        adminTeams: "Teams",
        adminProjects: "Projekte",
        adminTickets: "Tickets",
        adminPlans: "Pläne",
        adminAudit: "Audit",
        adminSettings: "Einstellungen"
      }
    }
  },
  "zh-CN": {
    translation: {
      common: { loadingGeoAnalytics: "正在加载 GeoAnalytics", loadingPage: "正在加载页面" },
      language: { change: "更改语言", current: "当前语言：{{language}}", menuTitle: "语言" },
      publicNav: { about: "了解公司", login: "登录", start: "开始" },
      login: {
        title: "登录",
        eyebrow: "面向真实运营的空间智能",
        headline: "GeoAnalytics",
        subtitle: "以更流畅的体验访问地图、项目、文档和团队。",
        email: "电子邮件",
        password: "密码",
        submit: "登录",
        google: "使用 Google 登录",
        createAccount: "创建账户",
        recoverPassword: "找回密码",
        divider: "或",
        verifying: "正在验证账户",
        secureAccess: "安全访问",
        secureAccessText: "支持 Firebase、Google 和邮箱/密码登录。",
        webVersion: "网页版",
        mobileVersion: "手机版"
      },
      shell: {
        profileTooltip: "用户资料",
        profileAria: "打开用户资料",
        signOut: "退出",
        notifications: "通知",
        openNotifications: "打开通知"
      },
      nav: {
        dashboard: "仪表盘",
        projects: "项目",
        newProcess: "新流程",
        studio: "REURB 工作室",
        teams: "团队",
        map: "地图",
        clients: "客户",
        registries: "登记",
        documents: "文档",
        tasks: "任务",
        reports: "报告",
        notifications: "通知",
        support: "支持",
        user: "用户",
        administration: "管理",
        adminArea: "管理",
        adminOverview: "概览",
        adminUsers: "用户",
        adminTeams: "团队",
        adminProjects: "项目",
        adminTickets: "工单",
        adminPlans: "计划",
        adminAudit: "审计",
        adminSettings: "设置"
      }
    }
  }
} as const;

void i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: defaultLanguage,
  supportedLngs: languageCodes,
  interpolation: {
    escapeValue: false
  },
  react: {
    useSuspense: false
  }
});

i18n.on("languageChanged", (languageCode) => {
  const language = getLanguageMeta(languageCode);

  document.documentElement.lang = language.code;
  document.documentElement.dir = "ltr";
  document.documentElement.dataset.languageDirection = language.dir;

  try {
    window.localStorage.setItem(languageStorageKey, language.code);
  } catch {
    // Local storage can be unavailable in restricted browser modes.
  }
});

const initialLanguage = getLanguageMeta(i18n.language);
document.documentElement.lang = initialLanguage.code;
document.documentElement.dir = "ltr";
document.documentElement.dataset.languageDirection = initialLanguage.dir;

export { i18n };
